import { ActionIcon, Button, Divider, Group, Loader, Stack, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconPlus, IconX } from "@tabler/icons-react";
import { Suspense, useState } from "react";
import { GroupSelector } from "shared/components/organization/groups/groups";
import { useAddUserToGroup } from "shared/graphql/groups";
import { useUserGroups, useUserGroupsWithChildren } from "shared/graphql/users";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useUserId } from "shared/stores/oidc";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { GroupLikeFragment } from "src/gql/graphql";
import { queryClient } from "src/queryClient";

const GroupsList = () => {
    const { userId } = useUserId();
    const { groups } = useUserGroups();
    const removeUser = useGraphqlMutation(mut);

    return groups.map((g) => (
        <Stack key={g.id}>
            <Group justify="space-between">
                <Title order={3} pl="sm">
                    {g.name}
                </Title>
                {g.internalJoinable && (
                    <ActionIcon
                        variant="transparent"
                        onClick={() => {
                            removeUser
                                .mutateAsync({ userId, groupId: g.id })
                                .then(() => queryClient.invalidateQueries({ queryKey: ["user", "id", userId] }))
                                .then(() => notify.show.success({ message: "Successfully left group" }))
                                .catch(notify.catch);
                        }}
                        aria-label="Leave group"
                    >
                        <IconX size={16} />
                    </ActionIcon>
                )}
            </Group>
            <Divider />
        </Stack>
    ));
};

const GroupSelectModal = () => {
    const { groups: memberGroups } = useUserGroups();
    const { groups: userGroups } = useUserGroupsWithChildren();
    const removeUser = useGraphqlMutation(mut);

    const [groups, setGroups] = useState<GroupLikeFragment[]>(memberGroups.filter((it) => it.internalJoinable));

    const { userId } = useUserId();
    const { addUserAsync } = useAddUserToGroup();

    return (
        <GroupSelector
            initialGroups={userGroups}
            selectedGroups={groups}
            onChange={(group, checked) => {
                if (checked) {
                    setGroups(groups.concat(group));
                    addUserAsync({ userId, groupId: group.id })
                        .then(() => notify.show.success("Successfully joined group"))
                        .catch(notify.catch);
                } else {
                    setGroups(groups.filter((it) => it.id != group.id));
                    removeUser
                        .mutateAsync({ userId, groupId: group.id })
                        .then(() => queryClient.invalidateQueries({ queryKey: ["user", "id", userId] }))
                        .then(() => notify.show.success("Successfully left group"))
                        .catch(notify.catch);
                }
            }}
            filter={(group) => group.internalJoinable}
            shouldDisable={(group) => !group.internalJoinable || group.synchronized}
        />
    );
};

const JoinGroupsContainer = () => {
    return (
        <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            type="button"
            onClick={() =>
                modals.open({
                    title: "Join Groups",
                    children: (
                        <Suspense fallback={<Loader />}>
                            <GroupSelectModal />
                        </Suspense>
                    ),
                })
            }
        >
            Join Groups
        </Button>
    );
};

export const GroupsContainer = () => {
    return (
        <Stack>
            <GroupsList />
            <JoinGroupsContainer />
        </Stack>
    );
};

const mut = graphql(`
    mutation SettingsSelfServiceGroupRemoveMember($userId: UUID!, $groupId: UUID!) {
        removeUserFromGroup(groupId: $groupId, userId: $userId) {
            id
        }
    }
`);
